import React, { useRef, useState, useEffect } from "react";
import { Box, Button, Checkbox, FormControlLabel, Grid, MenuItem, Select, TextField, Typography } from "@mui/material";
import {
    TranslationEnum,
    usePortalTranslation,
} from "../../providers/TranslationProvider";
import DefaultHeading from "../../components/heading/DefaultHeading";
import UserCard from "./userCard";
import RequestCard from "./RequestCard";
import SupervisorsList from "./SupervisorsList";
import SalesrepsList from "./SalesrepsList";
import RequestList from "./RequestList";
import axios from "axios";
import { Supervisor, SalesRep, Request } from "./types";
import { approveRequest, fetchRequests, fetchSalesreps, fetchSupervisors, formatAmount, rejectRequest } from "./manageRequestsService";
import { AnyAaaaRecord } from "dns";
import { openPopup } from "../../stateManagement/actions/popupActions";


export default function ManageRequests() {
    const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
    const [salesreps, setSalesreps] = useState<SalesRep[]>([]);
    const [requests, setRequests] = useState<Request[]>([]);
    const [loadingSupervisors, setLoadingSupervisors] = useState(false);
    const [loadingSalesreps, setLoadingSalesreps] = useState(false);
    const [loadingRequests, setLoadingRequests] = useState(false);

    const [selectedSalesrepId, setSelectedSalesrepId] = useState<string>("");
    const [selectedSupervisorId, setSelectedSupervisorId] = useState<string>("");
    const [statusFilter, setStatusFilter] = useState("All");

    const [searchSalesrep, setSearchSalesrep] = useState("");
    const [searchSupervisor, setSearchSupervisor] = useState("");
    const { translate } = usePortalTranslation();

    useEffect(() => {
        const fetchData = async () => {
            setLoadingSupervisors(true);
            setSearchSupervisor("");
            try {
                const resp = await fetchSupervisors();
                setSupervisors(resp ?? []);
            } catch (error) {
                console.error("Error fetching supervisors:", error);
            } finally {
                setLoadingSupervisors(false);
            }
        };

        fetchData();
    }, []);

    const handleSupervisorClick = async (supervisorId: string) => {
        setSelectedSupervisorId(supervisorId);
        if (!supervisorId) return;
        setLoadingSalesreps(true);
        setSalesreps([]);
        setRequests([]);
        setSelectedSalesrepId("");
        setStatusFilter("All");
        setSearchSalesrep("");
        setSearchSupervisor("");

        try {
            const resp = await fetchSalesreps(supervisorId);
            setSalesreps(resp ?? []);
        } catch (error) {
            console.error("Error fetching salesreps:", error);
        } finally {
            setLoadingSalesreps(false);
        }
    };

    const handleSalesrepClick = async (salesrepId: string) => {
        if (!salesrepId) return;
        setSelectedSalesrepId(salesrepId);
        setLoadingRequests(true);
        setRequests([]);
        setStatusFilter("All");

        try {
            const resp = await fetchRequests(selectedSupervisorId, salesrepId);
            setRequests(resp ?? []);
        } catch (error) {
            console.error("Error fetching requests:", error);
        } finally {
            setLoadingRequests(false);
        }
    };


    const handleApproveRequest = async (data: any, type: string, approval_id: string, currCreditLimit: string) => {
        const status = await approveRequest(approval_id, type, data, selectedSalesrepId);
        if (status == "approved" || status == "rfc_approved") {
            openPopup("Success", "Request Approved Successfully");
        } else if (status == "invalid") {
            openPopup("Alert", "Please enter credit increase details.");
        } else {
            openPopup("Error", "Network error - Approval Failed");
        }
        setLoadingRequests(true);
        const resp = await fetchRequests(selectedSupervisorId, selectedSalesrepId);
        setStatusFilter("All");
        setRequests(resp ?? data);
        setLoadingRequests(false);
    }

    const handleRejectRequest = async (data: any, type: string, approval_id: string) => {
        const status = await rejectRequest(approval_id, type, data, selectedSalesrepId);
        if (status == "rejected" || status == "rfc_rejected") {
            openPopup("Success", "Request Rejected Successfully");
        } else {
            openPopup("Error", "Network error - Rejection Failed");
        }
        setLoadingRequests(true);
        const resp = await fetchRequests(selectedSupervisorId, selectedSalesrepId);
        setStatusFilter("All");
        setRequests(resp ?? data);
        setLoadingRequests(false);
    }

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", px: "16px", }}>
            <Box sx={{ height: "90%", display: "flex", flexDirection: "column", width: "100%" }}>
                <DefaultHeading heading='Manage Request Approval' customFontColor="#4982a1" />

                <Box sx={{ flex: 1, my: 2, height: "100%" }}>
                    <Grid container spacing={2} sx={{ height: "100%" }}>

                        <Grid item xs={3} sx={{ height: "100%" }}>
                            <Box
                                sx={{
                                    bgcolor: "white",
                                    border: "1px solid #ccc",
                                    borderRadius: 2,
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "100%",
                                    overflow: "hidden",
                                }}
                            >
                                <Box sx={{ bgcolor: "#f3fbff", px: 2, py: 1.5 }}>
                                    <Typography sx={{ color: "#4982a1", fontWeight: "bold" }}>
                                        Select Supervisor
                                    </Typography>
                                </Box>

                                <Box sx={{ px: 2, pb: 1, pt: 2 }}>
                                    <Typography sx={{ color: "#7c7c7c", fontSize: 14, mb: "3px" }}>
                                        Select Supervisor<span style={{ color: "red" }}>*</span>
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        disabled={loadingSupervisors || supervisors.length == 0}
                                        placeholder="Search supervisor..."
                                        value={searchSupervisor}
                                        onChange={(e) => setSearchSupervisor(e.target.value)}
                                        sx={{
                                            backgroundColor: "#f5f5f5",
                                            borderRadius: 1,
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": { borderColor: "#e0e0e0" },
                                                "&:hover fieldset": { borderColor: "#bfbfbf" },
                                                "&.Mui-focused fieldset": { borderColor: "#24c6b1" },
                                            },
                                        }}
                                    />
                                </Box>

                                <SupervisorsList searchQuery={searchSupervisor} loading={loadingSupervisors}
                                    data={supervisors} onSupervisorClick={handleSupervisorClick} selectedSupervisorId={selectedSupervisorId} />

                            </Box>
                        </Grid>

                        <Grid item xs={3} sx={{ height: "100%" }}>
                            <Box
                                sx={{
                                    bgcolor: "white",
                                    border: "1px solid #ccc",
                                    borderRadius: 2,
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "100%",
                                    overflow: "hidden",
                                }}
                            >
                                <Box sx={{ bgcolor: "#f3fbff", px: 2, py: 1.5 }}>
                                    <Typography sx={{ color: "#4982a1", fontWeight: "bold" }}>
                                        SalesRep Mapped
                                    </Typography>
                                </Box>

                                <Box sx={{ px: 2, pb: 1, pt: 2 }}>
                                    <Typography sx={{ color: "#7c7c7c", fontSize: 14, mb: "3px" }}>
                                        Select SalesRep<span style={{ color: "red" }}>*</span>
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        disabled={loadingSupervisors || loadingSalesreps || salesreps.length == 0}
                                        placeholder="Search sales reps..."
                                        value={searchSalesrep}
                                        onChange={(e) => setSearchSalesrep(e.target.value)}
                                        sx={{
                                            backgroundColor: "#f5f5f5",
                                            borderRadius: 1,
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": { borderColor: "#e0e0e0" },
                                                "&:hover fieldset": { borderColor: "#bfbfbf" },
                                                "&.Mui-focused fieldset": { borderColor: "#24c6b1" },
                                            },
                                        }}
                                    />
                                </Box>

                                <SalesrepsList key={selectedSupervisorId} searchQuery={searchSalesrep} data={salesreps}
                                    loading={loadingSalesreps} onSalesrepClick={handleSalesrepClick} selectedSalesrepId={selectedSalesrepId} />

                            </Box>
                        </Grid>

                        <Grid item xs={6} sx={{ height: "100%" }}>
                            <Box
                                sx={{
                                    bgcolor: "white",
                                    color: "black",
                                    border: "1px solid #ccc",
                                    borderRadius: 2,
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    position: "relative",
                                    overflow: "hidden",
                                }}
                            >
                                <RequestList key={selectedSalesrepId} loading={loadingRequests} data={requests} statusFilter={statusFilter} onStatusFilterChange={setStatusFilter} onApprove={handleApproveRequest} onReject={handleRejectRequest} />
                            </Box>
                        </Grid>

                    </Grid>
                </Box>
            </Box>
        </Box>

    );
}
